import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Edit, User, X, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableMultiSelect } from "@/components/forms/SearchableMultiSelect";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminClient, useUpdateClient, useAdminSpecializations } from "@/hooks/api/use-admin";
import { useFormValidation, commonValidationRules } from "@/hooks/useFormValidation";
import type { UpdateClientRequest } from "@/api/types";

const EditClient = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isAdmin = user?.type === 'admin';

  const [formData, setFormData] = useState<UpdateClientRequest>({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    address: '',
    description: '',
    license_number: '',
    tax_id: '',
    specialization_ids: [],
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [documents, setDocuments] = useState<File[]>([]);
  
  // New state for the 6 specific document fields
  const [commercialRegister, setCommercialRegister] = useState<File | null>(null);
  const [taxCertificate, setTaxCertificate] = useState<File | null>(null);
  const [ibanCertificate, setIbanCertificate] = useState<File | null>(null);
  const [zakatIncomeCertificate, setZakatIncomeCertificate] = useState<File | null>(null);
  const [socialInsuranceCertificate, setSocialInsuranceCertificate] = useState<File | null>(null);
  const [companyProfile, setCompanyProfile] = useState<File | null>(null);

  // Enhanced validation (excluding password fields for edit)
  const { errors, validateField, validateForm, clearFieldError } = useFormValidation({
    name: commonValidationRules.name,
    email: commonValidationRules.email,
    phone: commonValidationRules.phone,
    company_name: commonValidationRules.companyName,
    address: commonValidationRules.address,
    license_number: commonValidationRules.licenseNumber,
    taxId: commonValidationRules.taxId,
    description: commonValidationRules.description,
  });

  // Fetch client data
  const { data: clientDetails, isLoading: clientLoading, error: clientError } = useAdminClient(
    parseInt(id || '0'),
    { enabled: isAdmin && !!id }
  );

  // Fetch specializations
  const { data: specializations, isLoading: specializationsLoading } = useAdminSpecializations(
    { enabled: isAdmin && !!user }
  );

  // Update mutation
  const updateMutation = useUpdateClient(() => {
    navigate(`/admin/dashboard/clients/${id}`);
  });

  // Load client data into form when available
  useEffect(() => {
    if (clientDetails?.data) {
      const responseData = (clientDetails.data as any).data || clientDetails.data;
      const clientData = responseData?.client || responseData;
      const userData = responseData?.user || clientData?.user || responseData;
      const specializationsData = Array.isArray(responseData?.specializations) ? responseData.specializations : (Array.isArray(clientData?.specializations) ? clientData.specializations : []);

      setFormData({
        name: userData?.name || '',
        email: userData?.email || '',
        company_name: clientData?.company_name || '',
        phone: clientData?.phone || '',
        address: clientData?.address || '',
        description: clientData?.description || '',
        license_number: clientData?.license_number || '',
        tax_id: clientData?.tax_id || '',
        specialization_ids: specializationsData.map((spec: any) => spec.id) || [],
      });
    }
  }, [clientDetails]);

  const handleInputChange = (field: keyof UpdateClientRequest, value: string | number | number[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Real-time validation for string fields
    if (typeof value === 'string') {
      const formValues = {
        name: formData.name || '',
        email: formData.email || '',
        phone: formData.phone || '',
        company_name: formData.company_name || '',
        address: formData.address || '',
        license_number: formData.license_number || '',
        taxId: formData.tax_id || '',
        description: formData.description || '',
      };
      const error = validateField(field, value, formValues);
      if (error) {
        // This will be handled by the validation hook
      } else {
        clearFieldError(field);
      }
    }
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicture(file);
    }
  };

  const handleDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setDocuments(prev => [...prev, ...files]);
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // New handlers for the 6 specific document fields
  const handleCommercialRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCommercialRegister(file);
  };

  const handleTaxCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setTaxCertificate(file);
  };

  const handleIbanCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setIbanCertificate(file);
  };

  const handleZakatIncomeCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setZakatIncomeCertificate(file);
  };

  const handleSocialInsuranceCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSocialInsuranceCertificate(file);
  };

  const handleCompanyProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCompanyProfile(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    // Validate form before submission
    const formValues = {
      name: formData.name || '',
      email: formData.email || '',
      phone: formData.phone || '',
      company_name: formData.company_name || '',
      address: formData.address || '',
      license_number: formData.license_number || '',
      taxId: formData.tax_id || '',
      description: formData.description || '',
    };

    const isValid = validateForm(formValues);

    if (!isValid) {
      return; // Don't submit if validation fails
    }

    const submitData: UpdateClientRequest = {
      ...formData,
      specialization_ids: formData.specialization_ids,
      ...(profilePicture && { profile_image: profilePicture }),
      ...(documents.length > 0 && { documents }),
      ...(commercialRegister && { commercial_register: commercialRegister }),
      ...(taxCertificate && { tax_certificate: taxCertificate }),
      ...(ibanCertificate && { iban_certificate: ibanCertificate }),
      ...(zakatIncomeCertificate && { zakat_income_certificate: zakatIncomeCertificate }),
      ...(socialInsuranceCertificate && { social_insurance_certificate: socialInsuranceCertificate }),
      ...(companyProfile && { company_profile: companyProfile }),
    };

    updateMutation.mutate({
      id: parseInt(id),
      data: submitData
    });
  };

  const isFormValid = () => {
    return (
      formData.name?.trim() !== '' &&
      formData.email?.trim() !== '' &&
      formData.company_name?.trim() !== '' &&
      formData.phone?.trim() !== '' &&
      Object.keys(errors).length === 0
    );
  };

  const { isRTL } = useLanguage();

  // Only admin can access
  if (!isAdmin) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center py-8 sm:py-12">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mb-3">
            <X className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('admin.clients.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}
          </p>
        </div>
      </div>
    );
  }

  if (clientLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-6xl mx-auto">
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="space-y-1 sm:space-y-2">
                <div className="h-6 sm:h-8 w-40 sm:w-64 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                <div className="h-3 sm:h-4 w-32 sm:w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              <div className="lg:col-span-1 space-y-4 sm:space-y-6">
                <div className="bg-white dark:bg-slate-800 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6">
                  <div className="aspect-square w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-slate-800 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6">
                  <div className="space-y-3 sm:space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="space-y-1 sm:space-y-2">
                        <div className="h-3 sm:h-4 w-20 sm:w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                        <div className="h-8 sm:h-10 w-full bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (clientError || !clientDetails?.data) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8 sm:py-12">
            <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mb-3">
              <X className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t('admin.clients.notFound') || 'العميل غير موجود'}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/dashboard/clients')}
              className="mt-4 text-xs sm:text-sm"
            >
              <ArrowLeft className={`w-3 h-3 sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('common.back') || 'العودة'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Extract data for display
  const responseData = (clientDetails.data as any).data || clientDetails.data;
  const clientData = responseData?.client || responseData;
  const profileImageUrl = responseData?.profile_image_url || clientData?.profile_image_url || (clientDetails.data as any).profile_image_url;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
          <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/admin/dashboard/clients/${id}`)}
                className="flex items-center gap-1 sm:gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
              >
                <ArrowLeft size={14} className={`sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <span className="text-xs sm:text-sm">{t('common.back') || 'العودة'}</span>
              </Button>
              <div className="hidden sm:block h-4 sm:h-6 w-px bg-slate-200 dark:bg-slate-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">
                  {t('admin.clients.edit') || 'تعديل العميل'}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 truncate">
                  {t('admin.clients.editClient') || 'تعديل بيانات العميل'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 overflow-x-hidden">

            {/* Left Column - Image and File Uploads */}
            <div className="w-full lg:w-1/3 min-w-0">
              <div className="space-y-4 sm:space-y-6">
                {/* Profile Picture Upload */}
                <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-slate-900 dark:text-slate-100">
                      {t('profile.profilePicture') || 'الصورة الشخصية'}
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="aspect-square w-24 h-24 sm:w-32 sm:h-32 mx-auto border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-700">
                      {profilePicture ? (
                        <img
                          src={URL.createObjectURL(profilePicture)}
                          alt="Profile preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt="Current profile"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <div className="text-center">
                          <User className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400 mx-auto mb-1 sm:mb-2" />
                          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">No image</p>
                        </div>
                      )}
                    </div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="cursor-pointer text-xs sm:text-sm w-full"
                    />
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 text-center">
                      {t('profile.allowedFiles') || 'JPG, PNG, GIF (Max 5MB)'}
                    </p>
                  </div>
                </CardContent>
              </Card>

                {/* Documents Upload */}
                <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-slate-900 dark:text-slate-100">
                      {t('profile.documents') || 'المستندات'}
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      <Input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleDocumentsChange}
                        className="cursor-pointer text-xs sm:text-sm w-full"
                      />
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                        {t('profile.allowedFiles') || 'PDF, DOC, DOCX, JPG, PNG'}
                      </p>

                      {/* Uploaded Documents List */}
                      {documents.length > 0 && (
                        <div className="space-y-2 sm:space-y-3">
                          <h4 className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">New Files to Upload:</h4>
                          {documents.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 dark:bg-slate-700 rounded">
                              <span className="text-xs sm:text-sm truncate flex-1 text-slate-700 dark:text-slate-300">{doc.name}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDocument(index)}
                                className="text-red-500 hover:text-red-700 h-6 w-6 sm:h-8 sm:w-8 p-0"
                              >
                                <X className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Form Fields */}
            <div className="w-full lg:w-2/3 min-w-0">
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4 sm:p-6">
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

                    {/* Personal Information Section */}
                    <div className="space-y-3 sm:space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold border-b border-slate-200 dark:border-slate-700 pb-2 text-slate-900 dark:text-slate-100">
                        {t('profile.personalInfo') || 'المعلومات الشخصية'}
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Name */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="name" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('auth.fullName') || 'الاسم الكامل'}
                          </Label>
                          <Input
                            id="name"
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder={t('auth.fullName') || 'أدخل الاسم الكامل'}
                            required
                            className={`text-xs sm:text-sm w-full ${errors.name ? 'border-red-500' : ''}`}
                          />
                          {errors.name && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.name}</p>
                          )}
                        </div>

                        {/* Email */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="email" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('auth.email') || 'البريد الإلكتروني'}
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder={t('auth.email') || 'أدخل البريد الإلكتروني'}
                            required
                            className={`text-xs sm:text-sm w-full ${errors.email ? 'border-red-500' : ''}`}
                          />
                          {errors.email && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.email}</p>
                          )}
                        </div>

                        {/* Phone */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="phone" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('auth.phone') || 'رقم الهاتف'}
                          </Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder={t('auth.phone') || 'أدخل رقم الهاتف'}
                            required
                            className={`text-xs sm:text-sm w-full ${errors.phone ? 'border-red-500' : ''}`}
                          />
                          {errors.phone && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.phone}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Company Information Section */}
                    <div className="space-y-3 sm:space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold border-b border-slate-200 dark:border-slate-700 pb-2 text-slate-900 dark:text-slate-100">
                        {t('profile.companyInfo') || 'معلومات الشركة'}
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Company Name */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="company_name" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('profile.companyName') || 'اسم الشركة'}
                          </Label>
                          <Input
                            id="company_name"
                            type="text"
                            value={formData.company_name}
                            onChange={(e) => handleInputChange('company_name', e.target.value)}
                            placeholder={t('profile.companyName') || 'أدخل اسم الشركة'}
                            required
                            className={`text-xs sm:text-sm w-full ${errors.company_name ? 'border-red-500' : ''}`}
                          />
                          {errors.company_name && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.company_name}</p>
                          )}
                        </div>

                        {/* License Number */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="license_number" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('profile.licenseNumber') || 'رقم الرخصة'}
                          </Label>
                          <Input
                            id="license_number"
                            type="text"
                            value={formData.license_number}
                            onChange={(e) => handleInputChange('license_number', e.target.value)}
                            placeholder={t('profile.licenseNumber') || 'أدخل رقم الرخصة'}
                            className={`text-xs sm:text-sm w-full ${errors.license_number ? 'border-red-500' : ''}`}
                          />
                          {errors.license_number && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.license_number}</p>
                          )}
                        </div>

                        {/* Tax ID */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="tax_id" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('profile.taxId') || 'الرقم الضريبي'}
                          </Label>
                          <Input
                            id="tax_id"
                            type="text"
                            value={formData.tax_id}
                            onChange={(e) => handleInputChange('tax_id', e.target.value)}
                            placeholder={t('profile.taxId') || 'أدخل الرقم الضريبي'}
                            className={`text-xs sm:text-sm w-full ${errors.taxId ? 'border-red-500' : ''}`}
                          />
                          {errors.taxId && (
                            <p className="text-xs sm:text-sm text-red-500">{errors.taxId}</p>
                          )}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="address" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                          {t('profile.address') || 'العنوان'}
                        </Label>
                        <Textarea
                          id="address"
                          value={formData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          placeholder={t('profile.address') || 'أدخل العنوان'}
                          rows={2}
                          className={`text-xs sm:text-sm w-full ${errors.address ? 'border-red-500' : ''}`}
                        />
                        {errors.address && (
                          <p className="text-xs sm:text-sm text-red-500">{errors.address}</p>
                        )}
                      </div>

                      {/* Description */}
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="description" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                          {t('profile.description') || 'الوصف'}
                        </Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          placeholder={t('profile.description') || 'أدخل وصف الشركة'}
                          rows={3}
                          className={`text-xs sm:text-sm w-full ${errors.description ? 'border-red-500' : ''}`}
                        />
                        {errors.description && (
                          <p className="text-xs sm:text-sm text-red-500">{errors.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Specializations */}
                    <div className="space-y-3 sm:space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold border-b border-slate-200 dark:border-slate-700 pb-2 text-slate-900 dark:text-slate-100">
                        {t('profile.specialization') || 'التخصصات'}
                      </h3>
                      {specializationsLoading ? (
                        <div className="space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-8 sm:h-10 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                          ))}
                        </div>
                      ) : (
                        <SearchableMultiSelect
                          options={specializations?.data || []}
                          selectedIds={formData.specialization_ids || []}
                          onChange={(selectedIds) => handleInputChange('specialization_ids', selectedIds)}
                          placeholder={t('profile.selectSpecializations') || 'اختر التخصصات...'}
                          searchPlaceholder={t('profile.searchSpecializations') || 'ابحث عن التخصصات...'}
                          noOptionsText={t('profile.noSpecializationsFound') || 'لم يتم العثور على تخصصات'}
                          maxVisible={3}
                        />
                      )}
                    </div>

                    {/* Required Documents Section */}
                    <div className="space-y-3 sm:space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold border-b border-slate-200 dark:border-slate-700 pb-2 text-slate-900 dark:text-slate-100">
                        {t('auth.requiredDocuments') || 'المستندات المطلوبة'}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                        {t('auth.documentsDesc') || 'يرجى رفع جميع المستندات المطلوبة لإكمال البيانات'}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Commercial Register */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="commercial_register" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.commercialRegister') || 'السجل التجاري'}
                          </Label>
                          <Input
                            id="commercial_register"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCommercialRegisterChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {commercialRegister && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {commercialRegister.name}
                            </p>
                          )}
                        </div>

                        {/* Tax Certificate */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="tax_certificate" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.taxCertificate') || 'شهادة الضريبة'}
                          </Label>
                          <Input
                            id="tax_certificate"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleTaxCertificateChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {taxCertificate && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {taxCertificate.name}
                            </p>
                          )}
                        </div>

                        {/* IBAN Certificate */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="iban_certificate" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.ibanCertificate') || 'شهادة IBAN'}
                          </Label>
                          <Input
                            id="iban_certificate"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleIbanCertificateChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {ibanCertificate && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {ibanCertificate.name}
                            </p>
                          )}
                        </div>

                        {/* Zakat Income Certificate */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="zakat_income_certificate" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.zakatCertificate') || 'شهادة الزكاة والدخل'}
                          </Label>
                          <Input
                            id="zakat_income_certificate"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleZakatIncomeCertificateChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {zakatIncomeCertificate && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {zakatIncomeCertificate.name}
                            </p>
                          )}
                        </div>

                        {/* Social Insurance Certificate */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="social_insurance_certificate" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.socialInsurance') || 'شهادة التأمينات الاجتماعية'}
                          </Label>
                          <Input
                            id="social_insurance_certificate"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleSocialInsuranceCertificateChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {socialInsuranceCertificate && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {socialInsuranceCertificate.name}
                            </p>
                          )}
                        </div>

                        {/* Company Profile */}
                        <div className="space-y-1 sm:space-y-2">
                          <Label htmlFor="company_profile" className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {t('documents.companyProfile') || 'ملف الشركة التعريفي'}
                          </Label>
                          <Input
                            id="company_profile"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCompanyProfileChange}
                            className="cursor-pointer text-xs sm:text-sm w-full"
                          />
                          {companyProfile && (
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                              Selected: {companyProfile.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-slate-200 dark:border-slate-700">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/admin/dashboard/clients/${id}`)}
                        className="w-full sm:w-auto text-xs sm:text-sm"
                      >
                        {t('common.cancel') || 'إلغاء'}
                      </Button>
                      <Button
                        type="submit"
                        disabled={updateMutation.isPending || !isFormValid()}
                        className="w-full sm:w-auto text-xs sm:text-sm"
                      >
                        {updateMutation.isPending ? (
                          <>
                            <div className={`w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('common.loading') || 'جاري التحميل...'}
                          </>
                        ) : (
                          <>
                            <Edit className={`w-3 h-3 sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('admin.clients.edit') || 'تعديل العميل'}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditClient;
